/* Grievance metadata shared by every grievance screen.
   Mirrors grievance/constants.py on the API — when a status, priority or
   resolution type is added there, add it here too or the UI falls back to the
   raw key. */

export const GRIEVANCE_STATUSES = [
    { value: "submitted", label: "Submitted" },
    { value: "acknowledged", label: "Acknowledged" },
    { value: "under_investigation", label: "Under Investigation" },
    { value: "awaiting_employee_response", label: "Awaiting Your Response" },
    { value: "escalated", label: "Escalated" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
    { value: "reopened", label: "Reopened" },
    { value: "withdrawn", label: "Withdrawn" },
];

export const GRIEVANCE_STATUS_LABELS = GRIEVANCE_STATUSES.reduce((acc, s) => {
    acc[s.value] = s.label;
    return acc;
}, {});

/* Explicit light/dark pairs per the house convention — theme tokens do not
   cover status semantics, so each pill names both variants. */
export const GRIEVANCE_STATUS_CHIP = {
    submitted: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
    acknowledged: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    under_investigation: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    awaiting_employee_response: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    escalated: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    reopened: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    withdrawn: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

export const GRIEVANCE_PRIORITIES = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
];

export const GRIEVANCE_PRIORITY_CHIP = {
    low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    medium: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

export const CONFIDENTIALITY_OPTIONS = [
    {
        value: "normal",
        label: "Normal",
        hint: "Your name is visible to whoever handles this grievance.",
    },
    {
        value: "confidential",
        label: "Confidential",
        hint: "Your name is visible to the handler only, and the record is kept out of manager views.",
    },
    {
        value: "anonymous",
        label: "Anonymous",
        hint: "Your name is hidden from everyone who handles this. You can still track it here.",
    },
];

export const RESOLUTION_TYPES = [
    { value: "resolved", label: "Resolved" },
    { value: "partially_resolved", label: "Partially Resolved" },
    { value: "not_upheld", label: "Not Upheld" },
    { value: "referred", label: "Referred to External Authority" },
    { value: "duplicate", label: "Duplicate" },
];

export const RESOLUTION_TYPE_LABELS = RESOLUTION_TYPES.reduce((acc, r) => {
    acc[r.value] = r.label;
    return acc;
}, {});

/* Audit verbs -> the sentence shown on the timeline. */
export const AUDIT_ACTION_LABELS = {
    raised: "Grievance raised",
    acknowledged: "Acknowledged",
    assigned: "Assigned",
    investigation_started: "Investigation started",
    info_requested: "Information requested",
    employee_responded: "Employee responded",
    escalated: "Escalated",
    resolved: "Resolved",
    closed: "Closed",
    auto_closed: "Auto-closed (reopen window lapsed)",
    reopened: "Reopened",
    withdrawn: "Withdrawn",
    commented: "Comment added",
    attachment_added: "Attachment added",
    attachment_removed: "Attachment removed",
    sla_breached: "SLA breached",
};

/* Statuses in which the grievance is still live. Kept in step with
   OPEN_STATUSES in grievance/constants.py. */
export const OPEN_STATUSES = [
    "submitted",
    "acknowledged",
    "under_investigation",
    "awaiting_employee_response",
    "escalated",
    "reopened",
];

export const ESCALATION_LEVEL_LABELS = {
    0: "Handler",
    1: "Escalation contact",
    2: "Management / HR",
};

/** Stored levels are 0/1/2; Categories and Inbox display them as L1/L2/L3. */
export const displayEscalationRung = (level) => {
    const stored = Number(level);
    if (!Number.isFinite(stored) || stored < 0) return 1;
    return stored + 1;
};

export const MAX_GRIEVANCE_FILE_MB = 5;

export const ALLOWED_GRIEVANCE_EXTENSIONS = [
    ".pdf", ".png", ".jpg", ".jpeg", ".doc", ".docx", ".xls", ".xlsx", ".txt",
];
